import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { getSimulator } from '../utils/MarketSimulator';

const MarketContext = createContext(null);

/**
 * Market Provider that runs the simulation loop and provides data to all consumers.
 * This replaces all API calls to the backend.
 */
export const MarketProvider = ({ children }) => {
  const simulatorRef = useRef(getSimulator());

  // Market data state (for SupplyDemandGraph, HorizontalNormalDistribution, Hero)
  const [marketData, setMarketData] = useState(null);
  const [marketHistory, setMarketHistory] = useState([]);

  // Candlestick data state (for Candlestick3D)
  const [candleData, setCandleData] = useState({
    closedCandles: [],
    liveCandle: null,
  });

  // Simulation running state
  const [isRunning, setIsRunning] = useState(true);

  // Update market data at 250ms interval (for price graphs)
  useEffect(() => {
    if (!isRunning) return;

    const updateMarketData = () => {
      const simulator = simulatorRef.current;
      const data = simulator.getCurrentMarketData();
      setMarketData(data);

      // Maintain history of last 60 data points
      setMarketHistory((prev) => {
        const newHistory = [...prev, data];
        if (newHistory.length > 60) {
          return newHistory.slice(-60);
        }
        return newHistory;
      });
    };

    // Initial update
    updateMarketData();

    const intervalId = setInterval(updateMarketData, 250);
    return () => clearInterval(intervalId);
  }, [isRunning]);

  // Update candlestick data at 500ms interval (for candlestick chart)
  useEffect(() => {
    if (!isRunning) return;

    const updateCandleData = () => {
      const simulator = simulatorRef.current;
      const data = simulator.getCandlesWithLive(4);
      setCandleData({
        closedCandles: data.closed,
        liveCandle: data.live,
      });
    };

    // Initial update
    updateCandleData();

    const intervalId = setInterval(updateCandleData, 500);
    return () => clearInterval(intervalId);
  }, [isRunning]);

  // Reset the simulator
  const reset = useCallback(() => {
    simulatorRef.current.reset();
    setMarketHistory([]);
    setMarketData(null);
    setCandleData({ closedCandles: [], liveCandle: null });
  }, []);

  // Pause/Resume simulation
  const toggleRunning = useCallback(() => {
    setIsRunning((prev) => !prev);
  }, []);

  const value = {
    // Market data for price displays
    marketData,
    marketHistory,

    // Candlestick data
    candleData,
    closedCandles: candleData.closedCandles,
    liveCandle: candleData.liveCandle,

    // All candles combined (closed + live)
    allCandles: candleData.liveCandle
      ? [...candleData.closedCandles, candleData.liveCandle]
      : candleData.closedCandles,

    // Controls
    reset,
    toggleRunning,
    isRunning,

    // Direct simulator access (if needed)
    simulator: simulatorRef.current,
  };

  return (
    <MarketContext.Provider value={value}>
      {children}
    </MarketContext.Provider>
  );
};

/**
 * Hook to access market context.
 */
export const useMarket = () => {
  const context = useContext(MarketContext);
  if (!context) {
    throw new Error('useMarket must be used within a MarketProvider');
  }
  return context;
};

/**
 * Hook specifically for market data (price, change, volume).
 */
export const useMarketData = () => {
  const { marketData, marketHistory } = useMarket();
  return { marketData, marketHistory };
};

/**
 * Hook specifically for candlestick data.
 */
export const useCandleData = () => {
  const { closedCandles, liveCandle, allCandles } = useMarket();
  return { closedCandles, liveCandle, allCandles };
};

export default MarketContext;
